import React, { useState, useEffect, useRef } from 'react';
import { Avatar, Box, Grid, IconButton, Paper, Typography, Dialog, Button, Tooltip, Modal } from '@mui/material';
import { store } from '../redux/store/index';
import { connect, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import ProfileForm from '../containers/Profile/Form';
import { ReactComponent as EditIcon } from '../assets/EditIcon.svg';
import * as providerActions from '../redux/actions/providerActions';
import ScheduleApp from './Schedule';
import { trackEvent } from '../utils/firebase';
import { getSignedImageUrl } from '../utils/service';
import { API } from '../api/api';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import QRCode from 'react-qr-code';

const Profile = ({ authReducer }) => {

  const [blurBg, setBlurBg] = useState(false)
  const [imageUrl, setImageUrl] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const payload = {
        entityType: "PROVIDER_PROFILE_IMAGE",
        mediaType: file.type,
        mediaSize: file.size,
        fileExtension: file.name ? "." + file.name.split(".").pop() : ".jpg",
      };

      const response = await API.post(
        '/v1/media/generate-signed-url-to-upload',
        payload
      );

    
      const uploadUrl = response.data?.uploadUrl || response.uploadUrl;
      const mediaId = response.data?.media?.id || response.media?.id;

      if (!uploadUrl || !mediaId) {
        console.error("Failed to get upload URL or media ID", response);
        return;
      }

      const xhr = new XMLHttpRequest();

      xhr.open("PUT", uploadUrl);
      xhr.setRequestHeader(
        "Content-Type",
        file.type || "application/octet-stream"
      );

      xhr.onload = async () => {
        if (xhr.status === 200 || xhr.status === 204) {
          setImageModalOpen(false);
          store.dispatch(providerActions.updateProviderDataRequestAction({
            profileImageMediaId: mediaId
          }));
          const newUrl = await getSignedImageUrl(mediaId);
          console.log("newUrl",newUrl)
          setImageUrl(newUrl);
        } else {
          console.error("Upload failed");
        }
      };

      xhr.onerror = (e) => {
        console.error("XHR error:", e);
      };

      xhr.send(file);

    } catch (error) {
      console.error("error", error);
    }
  };

  const handleClose = () => {
    setBlurBg((prev) => !prev)
  }

  const handleOpenQrModal = () => {
    setQrModalOpen(true);
    if (qrUrl) return;

    const shortUrl = authReducer?.userAttributes?.doctor?.shortUrl;
    if (shortUrl) {
      setQrUrl(shortUrl);
    }
  };

  useEffect(() => {
    trackEvent('profile_viewed');
    store.dispatch(providerActions.getProviderDetailsRequestAction());
  }, [])

  const providerReducer = useSelector((state) => state.providerReducer);

  const [profileData, setProfileData] = useState({})

  useEffect(() => {
    if (providerReducer?.providerInfo) {
      setProfileData(providerReducer?.providerInfo);
    }
  }, [providerReducer]);

  const getLimitedWords = (text, limit = 50) => {
    if (!text) return "";

    const words = text.trim().split(/\s+/);
    if (words.length <= limit) return text;

    return words.slice(0, limit).join(" ") + " ...";
};

useEffect(() => {
  let mounted = true;

  const loadImage = async () => {
    const mediaId = profileData?.basicInfo?.profileImageMediaId;

    if (!mediaId) return;

    const url = await getSignedImageUrl(mediaId);

    if (mounted) {
      setImageUrl(url);
    }
  };

  loadImage();

  return () => {
    mounted = false;
  };
}, [profileData?.basicInfo?.profileImageMediaId]);

  return (
    <>
      <Grid
        container
        direction="column"
        sx={{ p: 2, height: '100%', backgroundColor: '#FFFF', }}
      >
        <Grid container spacing={2} sx={{ width: '100%', pl: { md: 5, xl: 20 }, pr: { md: 5, xl: 14 }, }}>
          <Grid size={{ md: 8, sm: 12, xs: 12, lg: 8 }} >
            <Grid container spacing={3} p={2} >
              <Grid size={{ md: 6 }} >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box sx={{ position: 'relative' }}>
                    <Tooltip title={imageUrl ? "Tap to view & update":"Tap to upload"}>
                    <Avatar 
                      src={imageUrl || undefined} 
                      sx={{ width: 67, height: 67, cursor: 'pointer' }} 
                      onClick={() => {
                        if (imageUrl) {
                          setImageModalOpen(true);
                        } else {
                          fileInputRef.current?.click();
                        }
                      }}
                    />
                    </Tooltip>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                  </Box>
                  <Box sx={{ marginLeft: '20px', alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }} >
                    <Typography sx={{ fontFamily: 'Roboto', fontSize: '20px', fontWeight: '500', color: 'rgba(59, 54, 54, 1)' }}>
                      Dr. {profileData?.basicInfo?.firstName} {profileData?.basicInfo?.lastName}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Roboto', fontSize: '14px', fontWeight: '400' }}>
                      {profileData?.basicInfo?.designation}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Roboto', fontSize: '14px', fontWeight: '400' }}>
                      {profileData?.basicInfo?.experience} years of Experience
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ md: 1 }} />
              <Grid size={{ md: 4 }} sx={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }}  >
                <Typography sx={{ fontFamily: 'Roboto', fontWeight: '400', fontSize: '15px' }}>About me</Typography>
                <Typography sx={{ fontFamily: 'Roboto', fontWeight: '400', fontSize: '14px', textAlign: 'left' }}>{getLimitedWords(profileData?.about?.description, 25)}</Typography>
              </Grid>
              <Grid size={{ md: 1 }} />
            </Grid>
          </Grid>
          <Grid size={{ md: 4, sm: 12, xs: 12, lg: 4 }}  >
            <Grid container spacing={3} p={2} >
              <Grid size={{ md: 12 }} >
                <Box display="flex" justifyContent={'space-between'} gap={2} >
                  <Box textAlign="right" sx={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }} >
                    <Typography style={{ display: 'flex', flexDirection: 'row', fontFamily: 'Roboto', fontSize: '14px' }}>📞 <Typography style={{ marginLeft: '29px', fontFamily: 'Roboto', fontSize: '14px', color: 'rgba(0, 0, 0, 1)' }}>{profileData?.about?.contact?.phone}</Typography></Typography>
                    {profileData?.about?.contact?.email && <Typography style={{ display: 'flex', flexDirection: 'row', fontFamily: 'Roboto', fontSize: '14px' }}>@ <Typography style={{ marginLeft: '29px', fontFamily: 'Roboto', fontSize: '14px', color: 'rgba(0, 0, 0, 1)' }}>{profileData?.about?.contact?.email}</Typography></Typography>}
                    <Typography style={{ display: 'flex', flexDirection: 'row', fontFamily: 'Roboto', fontSize: '14px' }}>⏱ <Typography style={{ marginLeft: '29px', fontFamily: 'Roboto', fontSize: '14px', color: 'rgba(0, 0, 0, 1)' }}>{profileData?.basicInfo?.experience} years</Typography></Typography>
                  </Box>
                  <Box display="flex" gap={2}>
                    <Tooltip title="Show QR Code">
                      <QrCodeIcon 
                        style={{ cursor: 'pointer', color: '#666', fontSize: '24px' }} 
                        onClick={handleOpenQrModal} 
                      />
                    </Tooltip>
                    <EditIcon style={{ cursor: 'pointer', }} onClick={() => {
                      trackEvent('profile_edit_started');
                      setBlurBg(!blurBg)
                    }} />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <ScheduleApp />
      </Grid>
      {blurBg && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backdropFilter: 'blur(10px)',
            zIndex: 998,
          }}
        />
      )}

      {blurBg && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            borderRadius: 2,
            p: 3,
            backgroundColor: '#F2F2F2',
            width: '469px',
          }}
        >
          <IconButton
            onClick={() => { handleClose() }}
            sx={{
              position: 'absolute',
              top: 0,
              right: 1,
              zIndex: 1000,
            }}
          >
            <CloseIcon />
          </IconButton>
          <ProfileForm handleClose={handleClose} profileData={profileData} />
        </Paper>
      )}

      <Dialog open={imageModalOpen} onClose={() => setImageModalOpen(false)} maxWidth="sm" fullWidth>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
          <IconButton 
            onClick={() => setImageModalOpen(false)} 
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <img src={imageUrl} alt="Profile" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button variant="contained" onClick={() => fileInputRef.current?.click()}>
              Edit
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Modal
          open={qrModalOpen}
          onClose={() => setQrModalOpen(false)}
          aria-labelledby="qr-modal-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          BackdropProps={{
            sx: { backdropFilter: 'blur(5px)' }
          }}
        >
          <Box sx={{
            bgcolor: '#FFF',
            borderRadius: '16px',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: 24,
            position: 'relative'
          }}>
            <IconButton
              sx={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() =>  setQrModalOpen(false)}
            >
              <CloseIcon />
            </IconButton>
            <Typography id="qr-modal-title" variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
              Scan QR Code
            </Typography>
            { qrUrl ? (
            <QRCode value={qrUrl} size={300} />
          ) : (
            <Typography color="error">Failed to generate QR code</Typography>
          )}
          </Box>
        </Modal>
    </>
  );
};

const mapStateToProps = (state) => {
  const { authReducer } = state;
  return { authReducer };
};

export default connect(mapStateToProps)(Profile);
